import React, { useState } from 'react';
import { useAuth, useAudioPlayerState } from '../contexts/AuthContext';
import { stickerTextStyle } from './StickerText';
import { CloseIcon, SparklesIcon } from './Icons';
import { CustomAudioPlayer } from './AudioComponents';

interface AudioPlayerPillProps {
    onPostClick: (post: any) => void;
}

export const AudioPlayerPill: React.FC<AudioPlayerPillProps> = ({ onPostClick }) => {
    const { usersData, currentUser } = useAuth();
    const {
        activeAudioPost,
        closeAudioSession,
        audioPlayerOpen,
        setAudioPlayerOpen,
        audioQueue,
        setAudioQueue,
        currentQueueIndex,
        setCurrentQueueIndex,
        playNextTrack,
        playPrevTrack,
        playAudioPost
    } = useAudioPlayerState();

    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent, targetIndex: number) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === targetIndex) return;

        const updatedQueue = [...audioQueue];
        const [draggedItem] = updatedQueue.splice(draggedIndex, 1);
        updatedQueue.splice(targetIndex, 0, draggedItem);
        
        // Update currentQueueIndex to follow the currently playing track
        let newCurrentIndex = currentQueueIndex;
        if (draggedIndex === currentQueueIndex) {
            newCurrentIndex = targetIndex;
        } else if (draggedIndex < currentQueueIndex && targetIndex >= currentQueueIndex) {
            newCurrentIndex--;
        } else if (draggedIndex > currentQueueIndex && targetIndex <= currentQueueIndex) {
            newCurrentIndex++;
        }

        setAudioQueue(updatedQueue);
        setCurrentQueueIndex(newCurrentIndex);
        setDraggedIndex(null);
    };

    if (!audioPlayerOpen || !activeAudioPost) return null;

    const currentUserData = usersData[currentUser];
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;

    const hexToRgb = (hex: string) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : null;
    };

    const getExactModalBg = (color: string, opacityVal: number) => {
        const rgb = hexToRgb(color);
        if (!rgb) return 'rgba(0, 0, 0, 0.5)';
        return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacityVal})`;
    };

    const modalBg = getExactModalBg(userThemeColor, userOpacity);

    const authorData = usersData[activeAudioPost.author];
    const authorDisplayName = authorData?.displayName || activeAudioPost.author;
    const authorPfp = authorData?.pfp || `https://i.pravatar.cc/150?u=${activeAudioPost.author}`;

    const activeMediaItem = (() => {
        const media = (activeAudioPost.type === 'bump' || activeAudioPost.type === 'repost')
            ? activeAudioPost.originalPost?.content.media
            : activeAudioPost.content.media;
        return media?.find((m: any) => m.type === 'audio' || m.type === 'voice');
    })();
    const trackUrl = activeMediaItem?.url || '';
    const originalUrl = activeMediaItem?.originalUrl || '';
    const coverArt = (activeMediaItem as any)?.coverArt || '';

    return (
        <div 
            className="fixed inset-0 flex items-center justify-center z-50 p-4 animate-fade-in"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.65)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
            onClick={() => setAudioPlayerOpen(false)}
        >
            <div 
                className="rounded-[2.5rem] w-full max-w-md overflow-hidden flex flex-col p-6 border border-white/10 shadow-2xl relative max-h-[90vh] overflow-y-auto no-scrollbar"
                style={{ backgroundColor: modalBg, color: userTextColor }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <span 
                        className="text-xs font-bold uppercase tracking-wider"
                        style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}
                    >
                        Now Playing
                    </span>
                    <button 
                        onClick={() => setAudioPlayerOpen(false)}
                        className="text-white/60 hover:text-white transition-colors"
                        title="Hide Player"
                    >
                        <CloseIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Track Details */}
                <div className="flex items-center gap-4 bg-black/20 border border-white/10 rounded-2xl p-4">
                    <img 
                        src={coverArt || authorPfp} 
                        alt={authorDisplayName} 
                        className={`w-12 h-12 rounded-${coverArt ? 'xl' : 'full'} object-cover ring-2 ring-white/20`} 
                    />
                    <div className="flex-grow min-w-0">
                        <h4 
                            className="font-bold text-base truncate"
                            style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}
                        >
                            {authorDisplayName}
                        </h4>
                        <p 
                            className="text-xs opacity-70 truncate"
                            style={stickerTextStyle(0.18)}
                        >
                            {(() => {
                                const media = (activeAudioPost.type === 'bump' || activeAudioPost.type === 'repost')
                                    ? activeAudioPost.originalPost?.content.media
                                    : activeAudioPost.content.media;
                                const firstAudio = media?.find((m: any) => m.type === 'audio' || m.type === 'voice');
                                return firstAudio?.name || activeAudioPost.content?.text || (activeAudioPost.type === 'voice' ? 'Voice note' : 'Audio post');
                            })()}
                        </p>
                    </div>
                </div>

                {/* Shared audio player — same component as everywhere else */}
                <CustomAudioPlayer
                    post={activeAudioPost}
                    src={trackUrl}
                    authorId={activeAudioPost.author}
                />

                {/* Next / Prev Controls */}
                {audioQueue && audioQueue.length > 1 && (
                    <div className="flex justify-between items-center w-full gap-4 mt-1">
                        <button
                            onClick={playPrevTrack}
                            className="flex-grow bg-white/5 hover:bg-white/10 text-white font-bold py-2 px-4 rounded-xl border border-white/5 text-xs transition-all uppercase flex items-center justify-center gap-1.5"
                            style={stickerTextStyle(0.18)}
                        >
                            ⏮ Prev
                        </button>
                        <button
                            onClick={playNextTrack}
                            className="flex-grow bg-white/5 hover:bg-white/10 text-white font-bold py-2 px-4 rounded-xl border border-white/5 text-xs transition-all uppercase flex items-center justify-center gap-1.5"
                            style={stickerTextStyle(0.18)}
                        >
                            Next ⏭
                        </button>
                    </div>
                )}

                {/* Queue List */}
                {audioQueue && audioQueue.length > 1 && (
                    <div className="flex flex-col gap-2 mt-2 w-full">
                        <span className="text-[9px] font-bold uppercase tracking-widest text-white/50 px-1">
                            Queue ({currentQueueIndex + 1}/{audioQueue.length})
                        </span>
                        <div className="bg-black/20 border border-white/10 rounded-2xl p-2 max-h-[140px] overflow-y-auto no-scrollbar flex flex-col gap-1.5 w-full">
                            {audioQueue.map((track: any, index: number) => {
                                const isActive = index === currentQueueIndex;
                                const trackTitle = track.content?.text || (track.type === 'voice' ? 'Voice note' : 'Audio post');
                                return (
                                    <div
                                        key={`${track.id}-${index}`}
                                        onClick={() => playAudioPost(track)}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, index)}
                                        onDragOver={handleDragOver}
                                        onDrop={(e) => handleDrop(e, index)}
                                        className={`flex items-center gap-2 p-2 rounded-xl text-xs cursor-pointer transition-all hover:bg-white/5 ${isActive ? 'bg-yellow-400/20 border border-yellow-400/30' : 'border border-transparent'}`}
                                        style={{ opacity: index === draggedIndex ? 0.3 : 1 }}
                                    >
                                        <div className="text-white/20 hover:text-white/50 cursor-grab active:cursor-grabbing px-1" onClick={(e) => e.stopPropagation()}>
                                            ≡
                                        </div>
                                        <span className={`font-bold ${isActive ? 'text-yellow-400' : 'text-white/40'}`}>{index + 1}</span>
                                        <div className="flex-grow min-w-0">
                                            <p className={`font-semibold truncate text-left ${isActive ? 'text-yellow-400' : 'text-white'}`}>
                                                {trackTitle}
                                            </p>
                                            <p className="text-[10px] text-white/50 truncate text-left">
                                                @{track.author}
                                            </p>
                                        </div>
                                        {isActive && <span className="text-yellow-400 animate-pulse">●</span>}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2 mt-2 w-full">
                    <button
                        onClick={() => {
                            setAudioPlayerOpen(false);
                            onPostClick(activeAudioPost);
                        }}
                        className="flex-grow bg-white/10 hover:bg-white/20 text-white font-bold py-3 px-4 rounded-full transition-all border border-white/10 text-sm flex items-center justify-center gap-2"
                        style={stickerTextStyle(0.18)}
                    >
                        <SparklesIcon className="w-4 h-4" />
                        Open Post
                    </button>
                    <button
                        onClick={closeAudioSession}
                        className="flex-grow bg-red-600/80 hover:bg-red-600 text-white font-bold py-3 px-4 rounded-full transition-all text-sm"
                        style={stickerTextStyle(0.18)}
                    >
                        Close Session
                    </button>
                </div>
            </div>
        </div>
    );
};
